import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, getAuthHeaders } from '@/lib/queryClient';
import type { ChatMessage, ChatRoomInfo } from '@/lib/types';
import { useConnectionStatus } from './use-connection-status';
import { apiUrl } from '@/lib/config';

const isDev = import.meta.env.DEV;
const debug = (...args: unknown[]) => { if (isDev) console.log(...args); };

// How long to wait for the server's `message-sent` ack before marking an
// optimistic message as failed.
const SEND_ACK_TIMEOUT_MS = 10000;

export function useChat(userId: string | null, userLanguage?: string) {
  const { socket } = useConnectionStatus(userId);
  const [chatRooms, setChatRooms] = useState<ChatRoomInfo[]>([]);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [typing, setTyping] = useState<Record<string, string[]>>({});
  const [isCreatingRoom, setIsCreatingRoom] = useState<boolean>(false);
  // Tracks in-flight optimistic sends → their failure-timeout handles, so a
  // `message-sent` ack can cancel the timeout and reconcile the placeholder.
  const pendingSends = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const currentRoomRef = useRef<string | null>(null);
  currentRoomRef.current = currentRoom;

  // Set up message event listeners when socket or userLanguage changes
  useEffect(() => {
    if (!socket || !userId) {
      return;
    }

    debug('Setting up chat event listeners for user:', userId);

    // Handler for messages from other users
    const handleNewMessage = (message: ChatMessage) => {
      const isCurrentRoom = currentRoomRef.current === message.roomId;

      // Update chatRooms' lastMessage and unreadCount
      setChatRooms(prev => {
        return prev.map(room => {
          if (room.roomId !== message.roomId) return room;
          const isSender = message.senderId === userId;
          const newUnreadCount = (isCurrentRoom || isSender)
            ? 0
            : (room.unreadCount || 0) + 1;
          return {
            ...room,
            lastMessage: message,
            unreadCount: newUnreadCount,
          };
        });
      });

      setMessages(prev => {
        const roomMessages = prev[message.roomId] || [];
        const isDuplicate = roomMessages.some(m => m.messageId === message.messageId);
        if (isDuplicate) return prev;

        // Auto mark as read if user is currently in the room
        if (message.senderId !== userId && isCurrentRoom) {
          socket.emit('mark-read', { roomId: message.roomId, messageIds: [message.messageId] });
        }

        // Play notification sound if chat is not muted
        if (message.senderId !== userId) {
          const isMuted = localStorage.getItem(`chatMuteStatus_${message.roomId}`) === 'true';
          if (!isMuted) {
            try {
              const ctx = new AudioContext();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.frequency.value = 880;
              osc.type = 'sine';
              gain.gain.value = 0.15;
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
              osc.start(ctx.currentTime);
              osc.stop(ctx.currentTime + 0.15);
            } catch {}
          }
        }

        return {
          ...prev,
          [message.roomId]: [...roomMessages, message]
        };
      });
    };

    // Handler for message confirmation (sender only). Reconciles the optimistic
    // placeholder we rendered on send: replace it in place and mark it 'sent'.
    const handleMessageSent = (message: ChatMessage) => {
      setChatRooms(prev => {
        return prev.map(room => {
          if (room.roomId !== message.roomId) return room;
          return {
            ...room,
            lastMessage: message,
          };
        });
      });

      const pending = pendingSends.current[message.messageId];
      if (pending) {
        clearTimeout(pending);
        delete pendingSends.current[message.messageId];
      }
      setMessages(prev => {
        const roomMessages = prev[message.roomId] || [];
        const idx = roomMessages.findIndex(m => m.messageId === message.messageId);
        const confirmed: ChatMessage = { ...message, status: 'sent' };
        if (idx >= 0) {
          const copy = [...roomMessages];
          copy[idx] = { ...copy[idx], ...confirmed };
          return { ...prev, [message.roomId]: copy };
        }
        return { ...prev, [message.roomId]: [...roomMessages, confirmed] };
      });
    };

    // Handler for typing indicators
    const handleUserTyping = ({ userId: typingUserId, isTyping, roomId }: {
      userId: string;
      isTyping: boolean;
      roomId: string;
    }) => {
      setTyping(prev => {
        const roomTyping = prev[roomId] || [];
        if (isTyping) {
          return {
            ...prev,
            [roomId]: [...roomTyping.filter(id => id !== typingUserId), typingUserId]
          };
        } else {
          return {
            ...prev,
            [roomId]: roomTyping.filter(id => id !== typingUserId)
          };
        }
      });
    };

    // Handler for message deletion
    const handleMessageDeleted = ({ messageId }: { messageId: string }) => {
      setMessages(prev => {
        const newMessages = { ...prev };
        Object.keys(newMessages).forEach(roomId => {
          newMessages[roomId] = newMessages[roomId].filter(msg => msg.messageId !== messageId);
        });
        return newMessages;
      });
    };

    // Handler for room deletion (creator archived the room)
    const handleRoomDeleted = ({ roomId }: { roomId: string }) => {
      debug('Room deleted:', roomId);
      setChatRooms(prev => prev.filter(r => r.roomId !== roomId));
      setMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages[roomId];
        return newMessages;
      });
      if (currentRoom === roomId) {
        setCurrentRoom(null);
      }
    };

    // Handler for member leaving a room
    const handleRoomMemberLeft = ({ roomId }: { roomId: string }) => {
      debug('Member left room:', roomId);
      // Refresh room list to update member counts
      if (userId) {
        fetch(apiUrl(`/api/users/${userId}/rooms`))
          .then(res => res.json())
          .then(rooms => setChatRooms(rooms))
          .catch(() => {});
      }
    };

    // Register event listeners
    socket.on('new-message', handleNewMessage);
    socket.on('message-sent', handleMessageSent);
    socket.on('user-typing', handleUserTyping);
    socket.on('message-deleted', handleMessageDeleted);
    socket.on('room-deleted', handleRoomDeleted);
    socket.on('room-member-left', handleRoomMemberLeft);

    // Handle reaction updates
    const handleReactionUpdated = ({ messageId, reactions }: { messageId: string; reactions: Array<{ emoji: string; userIds: string[]; count: number }> }) => {
      setMessages(prev => {
        const updated = { ...prev };
        for (const roomId of Object.keys(updated)) {
          updated[roomId] = updated[roomId].map(msg =>
            msg.messageId === messageId ? { ...msg, reactions } : msg
          );
        }
        return updated;
      });
    };
    socket.on('reaction:updated', handleReactionUpdated);

    // Handle messages read status updates
    const handleMessagesRead = ({ roomId, userId: readerUserId, messageIds }: { roomId: string; userId: string; messageIds?: string[] }) => {
      if (readerUserId === userId) {
        setChatRooms(prev => prev.map(r => r.roomId === roomId ? { ...r, unreadCount: 0 } : r));
      }
      setMessages(prev => {
        const roomMessages = prev[roomId] || [];
        let changed = false;
        const updated = roomMessages.map(msg => {
          if (messageIds && messageIds.length > 0 && !messageIds.includes(msg.messageId)) return msg;
          const currentReadBy = msg.readBy || (msg.senderId ? [msg.senderId] : []);
          if (!currentReadBy.includes(readerUserId)) {
            changed = true;
            return {
              ...msg,
              readBy: [...currentReadBy, readerUserId],
              deliveryStatus: 'read' as const,
            };
          }
          return msg;
        });
        if (!changed) return prev;
        return { ...prev, [roomId]: updated };
      });
    };
    socket.on('messages-read', handleMessagesRead);

    // Cleanup function
    return () => {
      socket.off('new-message', handleNewMessage);
      socket.off('message-sent', handleMessageSent);
      socket.off('user-typing', handleUserTyping);
      socket.off('message-deleted', handleMessageDeleted);
      socket.off('room-deleted', handleRoomDeleted);
      socket.off('room-member-left', handleRoomMemberLeft);
      socket.off('reaction:updated', handleReactionUpdated);
      socket.off('messages-read', handleMessagesRead);
    };
  }, [socket, userId, userLanguage]);

  // Clear any outstanding send-failure timeouts on unmount.
  useEffect(() => {
    const pending = pendingSends.current;
    return () => {
      Object.values(pending).forEach(clearTimeout);
    };
  }, []);

  const loadUserRooms = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await apiRequest('GET', `/api/users/${userId}/rooms`);
      const rooms = await response.json();
      setChatRooms(rooms);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  }, [userId]);

  const loadMessages = useCallback(async (roomId: string) => {
    try {
      const url = userId
        ? `/api/rooms/${roomId}/messages?userId=${encodeURIComponent(userId)}`
        : `/api/rooms/${roomId}/messages`;
      const response = await apiRequest('GET', url);
      const roomMessages = await response.json();
      setMessages(prev => ({
        ...prev,
        [roomId]: roomMessages
      }));
      setChatRooms(prev => prev.map(r => r.roomId === roomId ? { ...r, unreadCount: 0 } : r));
      if (userId && socket && socket.connected) {
        socket.emit('mark-read', { roomId });
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }, [userId, socket]);

  const joinRoom = useCallback((roomId: string) => {
    if (currentRoom !== roomId) {
      setCurrentRoom(roomId);
    }
  }, [currentRoom]);

  // Handle room joining/leaving and message loading
  useEffect(() => {
    if (socket && currentRoom) {
      socket.emit('join-room', currentRoom);
      loadMessages(currentRoom);

      return () => {
        if (socket.connected) {
          socket.emit('leave-room', currentRoom);
        }
      };
    }
  }, [socket, currentRoom, loadMessages]);

  // Update the delivery status of an already-rendered (optimistic) message.
  const markMessageStatus = useCallback((roomId: string, messageId: string, status: ChatMessage['status']) => {
    setMessages(prev => {
      const roomMessages = prev[roomId] || [];
      const idx = roomMessages.findIndex(m => m.messageId === messageId);
      if (idx < 0) return prev;
      const copy = [...roomMessages];
      copy[idx] = { ...copy[idx], status };
      return { ...prev, [roomId]: copy };
    });
  }, []);

  // Emit a message and arm a failure timeout. If no `message-sent` ack arrives
  // within SEND_ACK_TIMEOUT_MS, the optimistic message is marked 'failed'.
  // Reverted from socket.volatile.emit (PR #44): volatile dropped voice sends
  // when the post-upload connection wasn't ready. Server-side messageId
  // idempotency makes a plain emit safe against reconnect-replay duplicates.
  const emitAndTrack = useCallback((messageData: any) => {
    if (!socket || !socket.connected) return; // optimistic message already 'failed'
    try {
      socket.emit('send-message', messageData);
      pendingSends.current[messageData.messageId] = setTimeout(() => {
        delete pendingSends.current[messageData.messageId];
        markMessageStatus(messageData.roomId, messageData.messageId, 'failed');
      }, SEND_ACK_TIMEOUT_MS);
    } catch (error) {
      console.error('Error emitting message:', error);
      markMessageStatus(messageData.roomId, messageData.messageId, 'failed');
    }
  }, [socket, markMessageStatus]);

  const sendMessage = useCallback((roomId: string, content: string, messageType = 'text', attachments?: any[]) => {
    if (!userId) {
      console.error('Cannot send message: No user ID');
      return;
    }
    if (!roomId) {
      console.error('Cannot send message: No room ID');
      return;
    }
    if (!content.trim() && (!attachments || attachments.length === 0)) {
      console.error('Cannot send message: Empty content and no attachments');
      return;
    }

    const connected = !!socket && socket.connected;
    const messageData = {
      messageId: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      roomId,
      senderId: userId,
      content: content.trim(),
      messageType,
      attachments: attachments || [],
      isTranslated: false,
      timestamp: new Date().toISOString()
    };

    // Render the message immediately (optimistic UI). It shows as 'sending'
    // until the server acks, 'failed' if we're offline or the ack times out.
    const optimistic: ChatMessage = {
      id: messageData.messageId,
      messageId: messageData.messageId,
      roomId,
      senderId: userId,
      senderName: localStorage.getItem('vyzle-chat-user-name') || 'You',
      content: messageData.content,
      messageType: messageType as ChatMessage['messageType'],
      attachments: attachments || [],
      isTranslated: false,
      timestamp: new Date(),
      status: connected ? 'sending' : 'failed',
    };
    setMessages(prev => {
      const roomMessages = prev[roomId] || [];
      if (roomMessages.some(m => m.messageId === optimistic.messageId)) return prev;
      return { ...prev, [roomId]: [...roomMessages, optimistic] };
    });

    emitAndTrack(messageData);
  }, [socket, userId, emitAndTrack]);

  // Retry a previously-failed message. Reuses the original messageId so the
  // server's idempotency guard prevents a duplicate if the first attempt did
  // land after all.
  const resendMessage = useCallback((message: ChatMessage) => {
    if (!message || !userId) return;
    const connected = !!socket && socket.connected;
    markMessageStatus(message.roomId, message.messageId, connected ? 'sending' : 'failed');
    if (!connected) return;
    emitAndTrack({
      messageId: message.messageId,
      roomId: message.roomId,
      senderId: userId,
      content: message.content,
      messageType: message.messageType,
      attachments: message.attachments || [],
      isTranslated: false,
      timestamp: new Date().toISOString(),
    });
  }, [socket, userId, markMessageStatus, emitAndTrack]);

  const createRoom = useCallback(async (name: string, type: 'direct' | 'group') => {
    if (!userId) {
      throw new Error('No user ID available for room creation');
    }

    if (isCreatingRoom) {
      throw new Error('Room creation already in progress');
    }

    setIsCreatingRoom(true);

    try {
      const roomData = {
        roomId: `room_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        name,
        type,
        createdBy: userId,
        inviteCode: Math.random().toString(36).substring(2, 11).toUpperCase()
      };

      const response = await apiRequest('POST', '/api/rooms', roomData);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`HTTP ${response.status}: ${errorData.error || 'Failed to create room'}`);
      }

      const room = await response.json();

      // Add creator as admin member
      const memberResponse = await apiRequest('POST', `/api/rooms/${room.roomId}/members`, {
        userId,
        isAdmin: true
      });

      if (!memberResponse.ok) {
        const memberError = await memberResponse.json().catch(() => ({ error: 'Unknown error' }));
        console.warn('Failed to add member:', memberResponse.status, memberError);
        // Continue despite member addition failure - room was created successfully
      }

      await loadUserRooms();
      return room;
    } catch (error) {
      throw error; // Re-throw to propagate to UI
    } finally {
      setIsCreatingRoom(false);
    }
  }, [userId, loadUserRooms, isCreatingRoom]);

  const joinRoomByInvite = useCallback(async (inviteCode: string) => {
    if (!userId) {
      throw new Error('You need to be signed in to join a chat');
    }

    try {
      // Plain fetch (not apiRequest, which throws on non-OK responses and
      // would swallow the structured error bodies handled below)
      const response = await fetch(apiUrl(`/api/rooms/invite/${inviteCode}`));
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Invalid invite code' }));
        throw new Error(errorData.error || 'Invalid invite code');
      }
      const room = await response.json();

      // Add user as member
      const memberResponse = await fetch(apiUrl(`/api/rooms/${room.roomId}/members`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({})
      });

      if (!memberResponse.ok) {
        const memberError = await memberResponse.json().catch(() => ({ error: 'Failed to join room' }));

        // Handle specific member limit errors
        if (memberResponse.status === 400) {
          if (memberError.error?.includes('limited to 2 members')) {
            throw new Error(`This direct chat is full (${memberError.currentMembers}/${memberError.maxMembers} members)`);
          }
          if (memberError.error?.includes('Group chat is full')) {
            throw new Error(memberError.error);
          }
        }

        throw new Error(memberError.error || 'Failed to join room as member');
      }

      // Force reload user rooms to ensure the new room appears
      await loadUserRooms();

      // Also update the chat rooms state immediately
      setChatRooms(prev => {
        const exists = prev.some(r => r.roomId === room.roomId);
        if (exists) return prev;
        return [...prev, room];
      });

      debug('Successfully joined room:', room.roomId, room.name);
      return room;
    } catch (error) {
      console.error('Failed to join room:', error);
      throw error; // Re-throw to allow proper error handling in UI
    }
  }, [userId, loadUserRooms]);

  const setUserTyping = useCallback((roomId: string, isTyping: boolean) => {
    if (socket && userId) {
      socket.emit('user-typing', { roomId, userId, isTyping });
    }
  }, [socket, userId]);
  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      // Notify server via socket to mark as deleted
      if (socket) {
        socket.emit('delete-message', { messageId });
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  }, [socket]);

  const reactToMessage = useCallback((messageId: string, emoji: string) => {
    if (socket && currentRoom) {
      socket.emit('reaction:add', { messageId, emoji, roomId: currentRoom });
    }
  }, [socket, currentRoom]);

  const unreactToMessage = useCallback((messageId: string, emoji: string) => {
    if (socket && currentRoom) {
      socket.emit('reaction:remove', { messageId, emoji, roomId: currentRoom });
    }
  }, [socket, currentRoom]);

  const markAsRead = useCallback((roomId: string, messageIds?: string[]) => {
    if (socket && userId && roomId) {
      socket.emit('mark-read', { roomId, messageIds });
      setChatRooms(prev => prev.map(r => r.roomId === roomId ? { ...r, unreadCount: 0 } : r));
    }
  }, [socket, userId]);

  useEffect(() => {
    if (userId) {
      loadUserRooms();
    }
  }, [userId, loadUserRooms]);

  return {
    chatRooms,
    messages,
    typing,
    currentRoom,
    joinRoom,
    sendMessage,
    resendMessage,
    deleteMessage,
    createRoom,
    joinRoomByInvite,
    setUserTyping,
    loadUserRooms,
    reactToMessage,
    unreactToMessage,
    markAsRead,
  };
}
